import { create } from "zustand";
import { persist, devtools } from "zustand/middleware";
import { createNavigationSlice } from "./slices/navigationSlice";
import { createFranchiseSlice } from "./slices/franchiseSlice";
import { createPostalCodeSlice } from "./slices/postalCodeSlice";
import { createServicesSlice } from "./slices/servicesSlice";
import { createConfigureationServicesSlice } from "./slices/configurationServicesSlice";
import { createCartSlice } from "./slices/cartSlice";
import { createPromotionSlice } from "./slices/promotionsSlice";
import { createPortableEquipmentSlice } from "./slices/portableEquipmentSlice";
import { createUserFormInformationSlice } from "./slices/userFormInformation";
import { createEstimateTypeSlice } from "./slices/estimateTypeSlice";
import { createScheduledAppointmentsSlice } from "./slices/scheduledAppointmentsSlice";
import { scrollToTopOfApp } from "../util/helper";

const useFormStore = create(
  // Use redux chrome devtools only in development
  devtools(
    // Store in localstore under CoitCleaningServicesForm
    persist(
      (set, get) => ({
        ...createNavigationSlice(set, get),
        ...createFranchiseSlice(set, get),
        ...createPostalCodeSlice(set, get),
        ...createServicesSlice(set, get),
        ...createConfigureationServicesSlice(set, get),
        ...createCartSlice(set, get),
        ...createPromotionSlice(set, get),
        ...createPortableEquipmentSlice(set, get),
        ...createUserFormInformationSlice(set, get),
        ...createEstimateTypeSlice(set, get),
        ...createScheduledAppointmentsSlice(set, get),

        // Form session tracking
        formStartTimestamp: null,
        formExpiredMessage: null,

        setFormStartTimestamp: () => {
          set({ formStartTimestamp: Date.now() });
        },

        setFormExpiredMessage: (message) => {
          set({ formExpiredMessage: message });
        },

        clearFormExpiredMessage: () => {
          set({ formExpiredMessage: null });
        },

        checkFormExpiration: () => {
          const state = get();
          const timestamp = state.formStartTimestamp;

          // No timestamp form not started yet
          if (!timestamp) {
            return false;
          }

          const now = Date.now();
          const expirationMinutesInMs = 60 * 60 * 1000;
          const elapsed = now - timestamp;

          return elapsed >= expirationMinutesInMs;
        },

        // Short lead form fields
        shortLeadFormPreferredDate: null,
        shortLeadFormNote: null,

        setShortLeadFormPreferredDate: (date) => {
          set({ shortLeadFormPreferredDate: date });
        },

        setShortLeadFormNote: (note) => {
          set({ shortLeadFormNote: note });
        },

        // Helper to find a service by ID in the franchise services array
        getServiceById: (serviceId) => {
          const { currentFranchise } = get();
          return currentFranchise?.services?.find((s) => s.id === serviceId);
        },

        // Reset/restart functionality
        resetAll: () => {
          set({
            currentScreen: "set-postal-code",
            isTransitioning: false,
            navigationHistory: [],
            currentPostalCode: null,
            currentFranchise: null,
            userFormInformation: {},
            selectedServices: [],
            selectedServicesConfiguration: {},
            appliedPromotions: [],
            needsPortableEquipment: false,
            manuallyEnteredPromoCodes: [],
            selectedEstimateType: "on_site_estimate_and_perform_services",
            estimateTypeManuallySelected: false,
            scheduledAppointments: [],
            formStartTimestamp: null,
            formExpiredMessage: null,
            shortLeadFormPreferredDate: null,
            shortLeadFormNote: null,
          });
          // Reset cart.
          get().resetCart();
          // Scroll to top of app after resetting
          scrollToTopOfApp();
        },
      }),
      {
        name: "CoitCleaningServicesForm",
        partialize: ({ isTransitioning, transitionMessage, ...rest }) => rest,
      },
    ),
    {
      enabled: process.env.NODE_ENV === "development",
      name: "DevCoitFormStore",
    },
  ),
);

export default useFormStore;
